import { ref } from 'vue'
import i18n from '@/locales'

interface ApiOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  debounce?: boolean
}

interface ApiResponse<T = unknown> {
  data: T | null
  error: string | null
  loading: boolean
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin + '/api/v1'

const pendingWrites = new Map<string, ReturnType<typeof setTimeout>>()
const DEBOUNCE_MS = 300

function getToken(): string | null {
  return localStorage.getItem('token')
}

function debounceWrite(key: string): Promise<void> {
  return new Promise((resolve) => {
    if (pendingWrites.has(key)) {
      clearTimeout(pendingWrites.get(key)!)
    }
    const timer = setTimeout(() => {
      pendingWrites.delete(key)
      resolve()
    }, DEBOUNCE_MS)
    pendingWrites.set(key, timer)
  })
}

function getErrorMessage(status: number, errData: Record<string, unknown>): string {
  const t = i18n.global.t
  if (errData.message) {
    return String(errData.message)
  }
  const key = `api.error.${status}`
  const translated = t(key)
  if (translated && translated !== key) {
    return translated
  }
  return t('api.error.default', { status })
}

export function useApi() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const request = async <T = unknown>(
    url: string,
    options: ApiOptions = {},
  ): Promise<ApiResponse<T>> => {
    const { method = 'GET', body = null, headers = {}, debounce: useDebounce = false } = options

    if (useDebounce && method !== 'GET') {
      const debounceKey = `${method}:${url}`
      await debounceWrite(debounceKey)
    }

    loading.value = true
    error.value = null

    try {
      const token = getToken()
      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-Timestamp': String(Date.now()),
        ...headers,
      }

      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`${BASE_URL}${url}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : null,
      })

      if (response.status === 401) {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken && !url.includes('/auth/')) {
          const refreshResult = await fetch(`${BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          })
          if (refreshResult.ok) {
            const refreshData = await refreshResult.json() as { token: string; refreshToken: string; user?: Record<string, unknown> }
            localStorage.setItem('token', refreshData.token)
            localStorage.setItem('refreshToken', refreshData.refreshToken)
            if (refreshData.user) {
              localStorage.setItem('user', JSON.stringify(refreshData.user))
            }
            requestHeaders['Authorization'] = `Bearer ${refreshData.token}`
            const retryResponse = await fetch(`${BASE_URL}${url}`, {
              method,
              headers: requestHeaders,
              body: body ? JSON.stringify(body) : null,
            })
            if (retryResponse.ok) {
              const retryData = (await retryResponse.json()) as T
              loading.value = false
              return { data: retryData, error: null, loading: false }
            }
          }
        }
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/login'
        return { data: null, error: 'Session expired', loading: false }
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({})) as Record<string, unknown>
        const errMsg = getErrorMessage(response.status, errData)
        error.value = errMsg
        loading.value = false
        return { data: null, error: errMsg, loading: false }
      }

      const data = (await response.json()) as T
      loading.value = false
      return { data, error: null, loading: false }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : i18n.global.t('api.error.network')
      error.value = errMsg
      loading.value = false
      return { data: null, error: errMsg, loading: false }
    }
  }

  const get = <T = unknown>(url: string) =>
    request<T>(url, { method: 'GET' })

  const post = <T = unknown>(url: string, body: unknown, debounce = true) =>
    request<T>(url, { method: 'POST', body, debounce })

  const put = <T = unknown>(url: string, body: unknown, debounce = true) =>
    request<T>(url, { method: 'PUT', body, debounce })

  const del = <T = unknown>(url: string, debounce = true) =>
    request<T>(url, { method: 'DELETE', debounce })

  return { loading, error, get, post, put, del, request }
}
